import React from 'react'
import { string, number, bool, oneOfType } from 'prop-types'
import { styled, Box, Typography, Grid } from '@mui/material'
import { useTest } from '@hooks/useTest'
import PriceAtom from '../Price/Price'
import ApplyButton from '../../../../components/shared/apply-button'

// Component Styles
const Container = styled(Grid)(({ theme }) => ({
  lineHeight: '1rem',
  fontFamily: theme.typography.fontFamily,
}))

const ContainerPrice = styled(Grid)(({ theme }) => ({
  padding: '8px 0 0 15px',
  [theme.breakpoints.up('md')]: {
    padding: '0 8px',
  },
}))

const Price = styled(Box, {
  shouldForwardProp: prop => !['customFontSize', 'sale'].includes(prop),
})(({ theme, sale, customFontSize }) => ({
  fontSize: customFontSize || '1.438rem',
  [theme.breakpoints.up('md')]: {
    fontSize: 30,
  },
  fontWeight: 600,
  color: `${sale ? theme.palette.error.main : theme.palette.primary.dark}`,
  '.strikethrough': {
    fontSize: 16,
  },
}))

const FreeShipping = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.error.main,
}))

const Financing = styled(Box)({
  fontSize: 16,
})

const RoomSavings = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  fontWeight: 400,

  'span:first-of-type': {
    fontSize: '14px',
    color: '#e31c3d',
    fontWeight: 500,
    marginRight: 5,
  },

  [theme.breakpoints.up('md')]: {
    marginLeft: '10px',
  },
}))

const StyledFinanceGrid = styled(Grid)(({ theme }) => ({
  a: {
    textTransform: 'capitalize',
    textDecoration: 'underline',
    lineHeight: 1.5,
    fontSize: '14px',
    padding: 0,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
    },
  },
  strong: {
    fontSize: '14px!important',
    fontWeight: '500!important',
  },
}))

const FinanceText = styled(Typography)(() => ({
  '& > *:nth-child(3)': {
    fontSize: '0.75rem!important',
    '*:not(sup)': {
      fontSize: '0.75rem!important',
    },
  },
}))

function ProductPrice({
  availability,
  freeShippingMessage,
  price,
  sale,
  strikeThrough,
  strikeThroughPrice,
  financeAmount,
  promotionalPrice,
  promotionalMonths,
  monthly,
  roomSavings,
  prequalifiedLink,
  className,
  customFontSize,
}) {
  const { generateTestId } = useTest()
  const showPromotionalFinanceInfo = promotionalPrice && promotionalMonths

  if (!availability) return null

  return (
    <Container container direction="column" spacing={1} alignItems="flex-start" className={className}>
      <ContainerPrice container direction="row" spacing={1} alignItems="center" className="container-price">
        <Box container sx={{ display: 'flex', flexDirection: 'row' }} className="container-price-info">
          {strikeThrough && (
            <Grid item sx={{ marginRight: '5px' }}>
              <Price data-testid={generateTestId('product-price', 'strikethrough')}>
                <PriceAtom className="strikethrough" strikeThrough price={strikeThroughPrice} />
              </Price>
            </Grid>
          )}
          <Grid item sx={{ paddingLeft: '0 !important' }}>
            <Price
              sale={sale || strikeThrough}
              customFontSize={customFontSize}
              data-testid={generateTestId('product', 'price')}
            >
              <PriceAtom price={price} />
            </Price>
          </Grid>
        </Box>
        {!!roomSavings && (
          <Grid item xl={8}>
            <RoomSavings container alignItems="baseline" flexWrap="wrap">
              <span style={{ whiteSpace: 'nowrap', color: '#e31c3d' }}>
                <PriceAtom price={roomSavings} />
                savings
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>(Compared to items sold separately)</span>
            </RoomSavings>
          </Grid>
        )}
      </ContainerPrice>
      {!!freeShippingMessage && (
        <Grid item sx={{ marginTop: '5px' }}>
          <FreeShipping>{freeShippingMessage}</FreeShipping>
        </Grid>
      )}
      {/* Finance threshold matching logic here :  https://github.com/RoomstoGoDigital/roomstogatsby/blob/dc2fe89044d911c2ef41e78761f74ffaac5ebfd7/src/@rtg2022/components/molecules/ProductTile/ProductTile.jsx#L338 */}
      {/* TODO this needs to be synced up but out of scope for this hotfix */}
      {!!financeAmount && financeAmount >= 5 && (
        <Grid item xs>
          <Financing
            data-testid={monthly ? null : generateTestId('product-price', 'finance-calculator')}
            component={monthly ? 'div' : 'p'}
          >
            {monthly ? (
              <StyledFinanceGrid container direction="row" alignItems={showPromotionalFinanceInfo ? 'start' : 'center'}>
                <FinanceText
                  data-testid={generateTestId('product-price', 'finance-calculator')}
                  fontSize="14px"
                  marginRight=".4rem"
                  style={{ color: '#e31c3d' }}
                >
                  {showPromotionalFinanceInfo && (
                    <>
                      <strong>
                        <PriceAtom price={promotionalPrice} />
                        <span aria-hidden="true">{` per Month First ${promotionalMonths} Months`}</span>
                      </strong>
                      <br />
                    </>
                  )}
                  <div>
                    {showPromotionalFinanceInfo && 'Then '}
                    <strong>
                      <PriceAtom price={financeAmount} />
                      <span aria-hidden="true">/mo.</span>
                      <span class="hide508">/ month</span>
                    </strong>{' '}
                    with <strong>{`${monthly} months financing*`}</strong>
                  </div>
                </FinanceText>
                <ApplyButton
                  asButton
                  url={prequalifiedLink}
                  category="pdp"
                  action="see-prequalify"
                  title="See if you Prequalify"
                  data-testid={generateTestId('prequalify', 'link')}
                >
                  See if you Prequalify
                </ApplyButton>
              </StyledFinanceGrid>
            ) : (
              `${financeAmount} per month*`
            )}
          </Financing>
        </Grid>
      )}
    </Container>
  )
}

ProductPrice.propTypes = {
  availability: bool,
  freeShippingMessage: string,
  price: string,
  sale: bool,
  strikeThrough: bool,
  strikeThroughPrice: oneOfType([number, string]),
  financeAmount: number,
  monthly: number,
  promotionalPrice: number,
  promotionalMonths: number,
  roomSavings: string,
  prequalifiedLink: string,
  className: string,
  customFontSize: number,
}

export default ProductPrice
